/**
 * Checkbox Component
 *
 * Simple checkbox for filter selections.
 * Figma: Checkbox (Node: 358:18210)
 *
 * Empty state (Node: 358:18211): 12px × 12px gray square (#D9D9D9)
 * Selected state (Node: 358:18213): Black checkmark icon only (12px × 9.4px)
 */
"use client";

import Image from "next/image";

interface CheckboxProps {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  variant?: "default" | "invisible"; // 'invisible' hides unchecked state completely
  className?: string;
  accentColor?: string; // Optional accent color for checked state
}

export function Checkbox({
  checked,
  onChange,
  disabled = false,
  variant = "default",
  className = "",
  accentColor,
}: CheckboxProps) {
  if (checked) {
    // Selected state: Just the checkmark icon, no box
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onChange?.(!checked);
        }}
        className={className}
        style={{
          width: "12px",
          height: "12px",
          border: "none",
          background: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          flexShrink: 0,
          padding: 0,
        }}
      >
        {/* Checkmark icon - 12px × 9.4px */}
        <div
          style={{
            width: "12px",
            height: "9.4px",
            position: "relative",
          }}
        >
          {accentColor ? (
            // Custom colored checkmark using SVG
            <svg
              width="12"
              height="9.4"
              viewBox="0 0 12 10"
              fill="none"
              style={{ display: "block" }}
            >
              <path
                d="M1 5L4.5 8.5L11 1"
                stroke={accentColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <Image
              src="/icons/check-small.svg"
              alt=""
              width={12}
              height={9.4}
              style={{
                display: "block",
                maxWidth: "none",
              }}
            />
          )}
        </div>
      </button>
    );
  }

  // Invisible variant: completely hidden when unchecked (for color section)
  if (variant === "invisible") {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onChange?.(!checked);
        }}
        className={className}
        style={{
          width: "12px",
          height: "12px",
          border: "none",
          background: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          flexShrink: 0,
          padding: 0,
        }}
      />
    );
  }

  // Default empty state: Gray filled square
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange?.(!checked);
      }}
      className={className}
      style={{
        width: "12px",
        height: "12px",
        border: "none",
        borderRadius: "0px",
        backgroundColor: "#D9D9D9", // Gray square from Figma
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
        padding: 0,
      }}
    />
  );
}
